import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Iconify, Label, Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { TableHeadCustom, TableNoData, TableRowLink } from '@/components/table';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { fDateTime } from '@/utils/formatTime';
import { getInAppPopupActionUsers } from '../api';
import {
  InAppPopup,
  InAppPopupActionEventType,
  InAppPopupActionUser,
  InAppPopupStats,
  InAppPopupStatsBreakdown,
} from '../types';
import {
  formatInAppPopupActionEventType,
  formatInAppPopupStatsCount,
  formatInAppPopupTapRate,
  getInAppPopupActionUserDisplayName,
  getInAppPopupActionUserPath,
} from '../utils';

type InAppPopupStatsDrawerProps = {
  open: boolean;
  popup?: InAppPopup | null;
  stats?: InAppPopupStats;
  onClose: VoidFunction;
};

const ACTION_USERS_TABLE_HEAD = [
  { id: 'user', label: 'User', minWidth: 180 },
  { id: 'email', label: 'Email', minWidth: 220 },
  { id: 'event_type', label: 'Action', minWidth: 150 },
  { id: 'count', label: 'Count', align: 'right', minWidth: 90 },
  { id: 'last_event_at', label: 'Last action', minWidth: 170 },
];

const EVENT_TYPE_OPTIONS: {
  value: '' | InAppPopupActionEventType;
  label: string;
}[] = [
  { value: '', label: 'All actions' },
  { value: 'button_tap', label: 'Button taps' },
  { value: 'dismiss', label: 'Dismissals' },
];

const safeDateTime = (value?: string | null) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return fDateTime(value);
};

const MetricItem = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 1,
      px: 2,
      py: 1.5,
      minWidth: 0,
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" sx={{ mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
);

const BreakdownList = ({
  title,
  items,
}: {
  title: string;
  items?: InAppPopupStatsBreakdown[];
}) => {
  const visibleItems = items?.filter((item) => item.count > 0) ?? [];

  if (!visibleItems.length) return null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Stack spacing={1}>
        {visibleItems.map((item, index) => (
          <Stack
            key={`${item.key ?? item.label ?? title}-${index}`}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              px: 1.5,
              py: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {item.label || item.key || item.action_type || 'Action'}
              </Typography>
              {item.action_type && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {item.action_type}
                </Typography>
              )}
            </Box>
            <Typography variant="subtitle2">
              {formatInAppPopupStatsCount(item.count)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const ActionUserCell = ({ user }: { user: InAppPopupActionUser }) => {
  const displayName = getInAppPopupActionUserDisplayName(user);
  const userPath = getInAppPopupActionUserPath(user);

  if (userPath) {
    return <TableRowLink linkText={displayName} linkTo={userPath} noWrap />;
  }

  return (
    <Typography variant="body2" noWrap>
      {displayName}
    </Typography>
  );
};

const ActionUsersTableRow = ({ user }: { user: InAppPopupActionUser }) => (
  <TableRow hover>
    <TableCell>
      <ActionUserCell user={user} />
    </TableCell>
    <TableCell>
      {user.email ? (
        <TableRowLink linkText={user.email} linkTo={`mailto:${user.email}`} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      )}
    </TableCell>
    <TableCell>
      <Stack spacing={0.5} alignItems="flex-start">
        <Label color={user.event_type === 'dismiss' ? 'warning' : 'info'}>
          {formatInAppPopupActionEventType(user.event_type)}
        </Label>
        {user.action_label && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {user.action_label}
          </Typography>
        )}
      </Stack>
    </TableCell>
    <TableCell align="right">
      {formatInAppPopupStatsCount(user.count)}
    </TableCell>
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      {safeDateTime(user.last_event_at)}
    </TableCell>
  </TableRow>
);

export default function InAppPopupStatsDrawer({
  open,
  popup,
  stats,
  onClose,
}: InAppPopupStatsDrawerProps) {
  const { errorMsg } = useSnackbarMsg();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [eventType, setEventType] = useState<'' | InAppPopupActionEventType>(
    ''
  );

  const { data, isLoading } = useQuery({
    queryKey: [
      'in-app-popup-action-users',
      popup?.id,
      page,
      rowsPerPage,
      eventType,
    ],
    queryFn: () =>
      getInAppPopupActionUsers(popup?.id ?? '', {
        page: page + 1,
        limit: rowsPerPage,
        event_type: eventType,
      }),
    enabled: open && Boolean(popup?.id),
    keepPreviousData: true,
    onError: (error) => {
      errorMsg(error, 'Unable to load popup action users');
    },
  });

  useEffect(() => {
    setPage(0);
    setEventType('');
  }, [popup?.id]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const actionUsers = data?.data ?? [];
  const totalActionUsers = data?.meta.total ?? actionUsers.length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: 1, md: 920 },
          maxWidth: '100vw',
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            {popup?.title || 'Popup stats'}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {popup?.id}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close popup stats">
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </Box>

      <Divider />

      <Scrollbar>
        <Stack spacing={3} sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(5, 1fr)',
              },
              gap: 1.5,
            }}
          >
            <MetricItem
              label="Views"
              value={formatInAppPopupStatsCount(stats?.impressions)}
            />
            <MetricItem
              label="Taps"
              value={formatInAppPopupStatsCount(stats?.button_taps)}
            />
            <MetricItem
              label="Dismissals"
              value={formatInAppPopupStatsCount(stats?.dismissals)}
            />
            <MetricItem
              label="Action users"
              value={formatInAppPopupStatsCount(stats?.action_users)}
            />
            <MetricItem
              label="Tap rate"
              value={formatInAppPopupTapRate(stats)}
            />
          </Box>

          {stats?.guest_action_users !== undefined &&
            stats.guest_action_users !== null && (
              <MetricItem
                label="Guest action users"
                value={formatInAppPopupStatsCount(stats.guest_action_users)}
              />
            )}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="stretch"
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <BreakdownList
                title="Button tap breakdown"
                items={stats?.button_tap_breakdown}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <BreakdownList
                title="Dismiss breakdown"
                items={stats?.dismiss_breakdown}
              />
            </Box>
          </Stack>

          <Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Box>
                <Typography variant="h6">Action users</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatInAppPopupStatsCount(totalActionUsers)}
                </Typography>
              </Box>
              <TextField
                select
                size="small"
                label="Action"
                value={eventType}
                onChange={(event) => {
                  setEventType(
                    event.target.value as '' | InAppPopupActionEventType
                  );
                  setPage(0);
                }}
                sx={{ minWidth: 180 }}
              >
                {EVENT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            {isLoading ? (
              <SkeletonLoadingTable />
            ) : (
              <>
                <Scrollbar>
                  <TableContainer sx={{ minWidth: 820 }}>
                    <Table size="small">
                      <TableHeadCustom headLabel={ACTION_USERS_TABLE_HEAD} />
                      <TableBody>
                        {actionUsers.map((user) => (
                          <ActionUsersTableRow
                            key={`${user.id}-${user.event_type}`}
                            user={user}
                          />
                        ))}

                        <TableNoData
                          isNotFound={!actionUsers.length}
                          title="No action users found"
                        />
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>

                {totalActionUsers > rowsPerPage && (
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalActionUsers}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_event, nextPage) => setPage(nextPage)}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                )}
              </>
            )}
          </Box>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
}
